import { Link, useLocation, useMatches } from "@tanstack/react-router";
import { ChevronDown, Handbag, Heart, Search, User } from "lucide-react";
import { useEffect, useRef, useState } from "react";
import type { ShopSearch } from "#/lib/shop-search";
import { cn } from "#/lib/utils";
import { ROUTES } from "../../lib/routes.js";
import { useCartCount, useRecoverCampaignCart } from "../../store/cart.js";
import { Badge } from "../ui/badge.js";

export interface HeaderProps {
  onOpenCart: () => void;
  onOpenSearch: () => void;
  logoSrc: string;
}

interface NavLink {
  label: string;
  to: string;
}

const BASE_NAV_LINKS: ReadonlyArray<NavLink> = [
  { label: "Home", to: ROUTES.home },
  { label: "Shop", to: ROUTES.shop },
  { label: "Sale", to: ROUTES.sale },
  { label: "FAQs", to: ROUTES.faqs },
  { label: "Contact Us", to: ROUTES.contact },
  { label: "Track Your Order", to: ROUTES.track },
];

interface ShopMenuItem {
  label: string;
  to?: string;
  search?: Partial<ShopSearch>;
  disabled?: boolean;
}

interface ShopGroup {
  heading: string;
  items: ReadonlyArray<ShopMenuItem>;
}

interface ShopColumn {
  groups: ReadonlyArray<ShopGroup>;
}


const SHOP_COLUMNS: ReadonlyArray<ShopColumn> = [
  {
    groups: [
      {
        heading: "Books",
        items: [
          {
            label: "Textbooks & Workbooks",
            search: { publisher : [
                "bju-press",
                "apologia",
                "rex-bookstore",
                "ici-ministries",
                "c-e-publishing",
                "sibs-publishing-house",
                "brilliant-creations"
              ] },
          },
          {
            label: "Children's Books",
            search: { publisher: ["tyndale-house-publishers", "tahanan-books"]  },
          },
          { label: "Literature & Classics",
            search: { publisher: ["total-language-plus", "living-book-press"] },
          },
          { label: "Family Books", search: { subject: ["english"] } },
        ],
      },
    ],
  },
  {
    groups: [
      {
        heading: "Curriculum Sets",
        items: [
          { label: "BJU DLO", search: { bundles: ["bju"] } },
          { label: "AOP Ignitia", search: { bundles: ["aop"] } },
          { label: "LIFEPAC", search: { bundles: ["lifepac"] } },
          { label: "BJU Sets", search: { bundles: ["bju-sets"] } },
        ],
      },
      {
        heading: "Homeschool Style",
        items: [
          { label: "Traditional", search: { homeschool: ["traditional"] } },
          {
            label: "Charlotte Mason",
            search: { homeschool: ["charlotte-mason"] },
          },
          { label: "Classical", search: { homeschool: ["classical"] } },
        ],
      },
    ],
  },
  {
    groups: [
      {
        heading: "Digital",
        items: [
          { label: "Ebooks", search: { category: ["ebook"] } },
          {
            label: "Online Subscriptions",
            search: { category: ["online-materials"] },
          },
          { label: "Quipper", search: { publisher: ["quipper"] } },
        ],
      },
    ],
  },
  {
    groups: [
      {
        heading: "Grade Levels",
        items: [
          { label: "Nursery", search: { grade: ["nursery"] } },
          { label: "Pre-K", search: { grade: ["pre-kinder"] } },
          { label: "Kinder", search: { grade: ["kinder"] } },
          {
            label: "Grade 1-3",
            search: { grade: ["grade-1", "grade-2", "grade-3"] },
          },
          {
            label: "Grade 4-6",
            search: { grade: ["grade-4", "grade-5", "grade-6"] },
          },
          {
            label: "Grade 7-9",
            search: { grade: ["grade-7", "grade-8", "grade-9"] },
          },
          {
            label: "Grade 10-12",
            search: { grade: ["grade-10", "grade-11", "grade-12"] },
          },
        ],
      },
    ],
  },
  {
    groups: [
      {
        heading: "Brands",
        items: [
          {
            label: "Tyndale",
            search: { publisher: ["tyndale-house-publishers"] },
          },
          {
            label: "Living Book Press",
            search: { publisher: ["living-book-press"] },
          },
          { label: "Apologia", search: { publisher: ["apologia"] } },
          { label: "BJU Press", search: { publisher: ["bju-press"] } },
          {
            label: "Lifepac",
            search: { publisher: ["alpha-omega-publications"] },
          },
          { label: "Master Books", search: { publisher: ["masterbooks"] } },
          {
            label: "Tahanan Books",
            search: { publisher: ["tahanan-books"] },
          },
          { label: "See all brands", to: ROUTES.shop },
        ],
      },
    ],
  },
];

function useRouteTitle(): string {
  const matches = useMatches();
  for (let i = matches.length - 1; i >= 1; i--) {
    const meta = matches[i]?.meta;
    if (!meta) continue;
    for (const tag of meta) {
      if (
        tag &&
        typeof tag === "object" &&
        "title" in tag &&
        typeof tag.title === "string" &&
        tag.title.length > 0
      ) {
        return tag.title;
      }
    }
  }
  return "";
}

export function Header({ onOpenCart, onOpenSearch, logoSrc }: HeaderProps) {
  const count = useCartCount();
  useRecoverCampaignCart();
  const [hidden, setHidden] = useState(false);
  const [shopOpen, setShopOpen] = useState(false);

  const closeTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const lastYRef = useRef(0);
  const location = useLocation();
  const pathname = location.pathname;
  const title = useRouteTitle();

  useEffect(() => {
    const onScroll = () => {
      const y = window.scrollY;
      const dy = y - lastYRef.current;
      if (y > 80 && dy > 4) setHidden(true);
      else if (dy < -4 || y <= 8) setHidden(false);
      lastYRef.current = y;
    };
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const openShop = () => {
    if (closeTimerRef.current) {
      clearTimeout(closeTimerRef.current);
      closeTimerRef.current = null;
    }
    setShopOpen(true);
  };

  const closeShop = () => {
    closeTimerRef.current = setTimeout(() => setShopOpen(false), 120);
  };

  return (
    <header
      className={cn(
        "sticky top-0 z-40 w-full transition-transform duration-300",
        "bg-background/80 backdrop-blur-[2px] shadow-[0_4px_16px_-4px_rgba(0,0,0,0.2)] border-b border-border/20",
        hidden ? "-translate-y-full lg:translate-y-0" : "translate-y-0",
      )}
    >
      {/* Mobile Nav */}
      <div className="mx-auto w-full px-6 md:px-10 flex h-20 items-center lg:hidden gap-4">
        <Link
          to={ROUTES.home}
          className="flex items-center gap-2 shrink-0"
          aria-label="Learning Plus home"
        >
          <img
            src={logoSrc}
            alt="Learning Plus"
            width={48}
            height={48}
            className="h-12 w-12 object-cover"
            fetchPriority="high"
          />
        </Link>
        <h1 className="m-0 min-w-0 flex-1 truncate text-base font-semibold text-foreground">
          {title}
        </h1>
        <button
          type="button"
          onClick={onOpenSearch}
          aria-label="Search"
          className="inline-flex h-10 w-10 shrink-0 items-center justify-center rounded-full border-0 bg-transparent cursor-pointer text-[#89AC7E] hover:text-[#233D15]"
        >
          <Search className="size-5" />
        </button>
      </div>

      {/* Desktop Nav */}
      <div className="mx-auto w-full max-w-6xl px-6 md:px-10 hidden h-20 items-center lg:grid lg:grid-cols-[1fr_auto_1fr] gap-6">
        <div className="flex items-center justify-start">
          <Link to={ROUTES.home} className="flex items-center gap-2 shrink-0">
            <img
              src={logoSrc}
              alt="Learning Plus"
              width={64}
              height={64}
              className="h-16 w-16 object-cover"
              fetchPriority="high"
            />
          </Link>
        </div>

        <nav className="flex justify-center gap-10 items-center">
          {BASE_NAV_LINKS.map((link) => {
            const active = pathname === link.to;

            if (link.to === ROUTES.shop) {
              const highlighted = active || shopOpen;
              return (
                <div key={link.to} className="relative">
                  <Link
                    to={link.to}
                    onMouseEnter={openShop}
                    onMouseLeave={closeShop}
                    className={cn(
                      "relative text-sm transition-colors inline-flex items-center gap-1 font-semibold text-[#89AC7E] hover:text-[#233D15] py-2 border-b-2 border-transparent hover:border-[#233D15] transition-all duration-300 ease-in-out",
                      highlighted && "text-[#233D15] border-[#233D15]",
                    )}
                  >
                    {link.label}
                    <ChevronDown
                      className={cn(
                        "size-4 transition-transform duration-200",
                        shopOpen && "rotate-180",
                      )}
                    />
                  </Link>
                </div>
              );
            }

            return (
              <Link
                key={link.to}
                to={link.to}
                className={cn(
                  "text-sm font-semibold text-[#89AC7E] hover:text-[#233D15] py-2 border-b-2 border-transparent hover:border-[#233D15] transition-all duration-300 ease-in-out",
                  active && "text-[#233D15] border-[#233D15]",
                )}
              >
                {link.label}
              </Link>
            );
          })}
        </nav>

        {/* Action Icons */}
        <div className="flex items-center justify-end gap-0 shrink-0 text-[#89AC7E]">
          <button
            type="button"
            onClick={onOpenSearch}
            aria-label="Search"
            className="inline-flex h-8 w-8 items-center justify-center rounded-full bg-transparent border-0 cursor-pointer hover:text-[#233D15] transition-colors"
          >
            <Search className="size-5" strokeWidth={2} />
          </button>

          <Link
            to={ROUTES.accountWishlist}
            aria-label="Wishlist"
            className="inline-flex h-8 w-8 items-center justify-center rounded-full hover:text-[#233D15] transition-colors"
          >
            <Heart className="size-5" strokeWidth={2} />
          </Link>

          <button
            type="button"
            onClick={onOpenCart}
            aria-label="Open cart"
            className="relative inline-flex h-8 w-8 items-center justify-center rounded-full bg-transparent border-0 cursor-pointer hover:text-[#233D15] transition-colors"
          >
            <Handbag className="size-5" strokeWidth={2} />
            {count > 0 && (
              <Badge
                variant="default"
                className="absolute -top-1 -right-1 h-5 min-w-5 px-1 justify-center text-[10px] leading-none bg-[#89AC7E]"
              >
                {count}
              </Badge>
            )}
          </button>

          <Link
            to={ROUTES.account}
            aria-label="Account"
            className="inline-flex h-8 w-8 items-center justify-center rounded-full hover:text-[#233D15] transition-colors"
          >
            <User className="size-5" strokeWidth={2} />
          </Link>
        </div>
      </div>

      <nav
        aria-label="Shop categories"
        onMouseEnter={openShop}
        onMouseLeave={closeShop}
        className={cn(
          "absolute top-full left-0 right-0 hidden lg:grid transition-[grid-template-rows,opacity,background-color,border-color,box-shadow] duration-300 ease-out",
          "bg-background/95 backdrop-blur-xl border-b border-border/50 shadow-[0_10px_20px_-10px_rgba(0,0,0,0.1)]",
          shopOpen
            ? "grid-rows-[1fr] opacity-100"
            : "grid-rows-[0fr] opacity-0 pointer-events-none",
        )}
      >
        <div className="min-h-0 overflow-hidden">
          <div className="mx-auto w-full max-w-6xl px-6 md:px-10 py-8 grid grid-cols-5 gap-8">
            {SHOP_COLUMNS.map((column, i) => (
              <div key={i} className="flex flex-col gap-5">
                {column.groups.map((group) => (
                  <div key={group.heading} className="flex flex-col gap-2">
                    <div className="font-semibold font-display text-sm">
                      {group.heading}
                    </div>
                    {group.items.length > 0 && (
                      <div className="flex flex-col">
                        {group.items.map((item) => {
                          const linkClass =
                            "py-1 text-sm text-foreground/70 hover:text-[#89AC7E] transition-colors";
                          if (item.disabled) {
                            return (
                              <span
                                key={`${group.heading}-${item.label}`}
                                aria-disabled="true"
                                title="Coming soon"
                                className="py-1 text-sm text-foreground/40 cursor-not-allowed"
                              >
                                {item.label}
                              </span>
                            );
                          }
                          if (item.search) {
                            return (
                              <Link
                                key={`${group.heading}-${item.label}`}
                                to="/shop"
                                search={{
                                  ...item.search,
                                  page: 1,
                                  sort: "default",
                                }}
                                className={linkClass}
                              >
                                {item.label}
                              </Link>
                            );
                          }
                          return (
                            <Link
                              key={`${group.heading}-${item.label}`}
                              to={item.to ?? ROUTES.shop}
                              className={linkClass}
                            >
                              {item.label}
                            </Link>
                          );
                        })}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            ))}
          </div>
        </div>
      </nav>
    </header>
  );
}
